import { useAuth } from "@/lib/auth-context";
import {
  decryptWithKey,
  encryptWithKey,
  generateRandomBytes,
} from "@/lib/crypto/masterKey";
import {
  finishOpaqueLogin,
  finishOpaqueRegister,
  startOpaqueLogin,
  startOpaqueRegister,
} from "@/lib/crypto/opaque";
import { useVaultStore } from "@/stores/vault-store";
import {
  getCurrentAuthUser,
  listOwnedVaults,
  loginOpaqueFinish,
  loginOpaqueStart,
  registerOpaqueFinish,
  registerOpaqueStart,
  resetVault,
  type OwnedVaultItem,
} from "@secure-vault/api";
import { Button, ConfirmDialog, Input, Label } from "@secure-vault/ui";
import {
  AlertTriangle,
  ArrowLeft,
  Eye,
  EyeOff,
  KeyRound,
  Loader2,
  Lock,
  ShieldAlert,
} from "lucide-react";
import React, { useEffect, useRef, useState } from "react";
import { toast } from "sonner";

type ViewState = "loading" | "login" | "setup" | "reset" | "success";

/**
 * Inline vault unlock / set-password panel. Rendered by an owner route
 * (`/files`, `/trustees`) in place of its normal content while the vault is
 * locked, inside the app chrome (header + nav stay visible so the owner can
 * navigate to `/` or `/settings` without unlocking). It picks between creating
 * a password ("setup") and entering an existing one ("login") from the server
 * `opaque_registered` flag, and includes the forgot-password reset flow.
 *
 * On success it calls `setVaultSession(...)`, flipping the store to
 * `isUnlocked`; the rendering container then swaps this panel out for its real
 * view — no callback needed.
 */
export function UnlockVaultPanel() {
  const { user } = useAuth();
  const { isUnlocked, setVaultSession } = useVaultStore();

  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [viewState, setViewState] = useState<ViewState>("loading");

  const goToReset = () => {
    setError(null);
    setViewState("reset");
  };

  const backToLogin = () => {
    setViewState("login");
  };

  useEffect(() => {
    if (!user?.email) return;
    getCurrentAuthUser()
      .then((me) => setViewState(me.opaque_registered ? "login" : "setup"))
      .catch(() => setViewState("login"));
  }, [user?.email]);

  if (isUnlocked) return null;
  // Don't render a half-panel (header + spinner) while we resolve login-vs-setup;
  // wait until the view is known so the header and form appear together at once.
  if (viewState === "loading") return null;

  const handleUnlock = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!password || !user?.email) return;

    setLoading(true);
    setError(null);

    try {
      const loginStartResult = await startOpaqueLogin(password);

      let initResponse;
      try {
        initResponse = await loginOpaqueStart(
          user.email,
          loginStartResult.startLoginRequest
        );
      } catch (err: unknown) {
        const errMsg =
          (err as Error & { error?: string })?.message ||
          (err as Error & { error?: string })?.error ||
          "";
        if (
          errMsg.toLowerCase().includes("not found") ||
          errMsg.toLowerCase().includes("invalid email or password")
        ) {
          setViewState("setup");
          setLoading(false);
          return;
        }
        throw err;
      }

      const loginFinishResult = await finishOpaqueLogin(
        loginStartResult.clientLoginState,
        initResponse.opaque_ke2
      );

      const finishResponse = await loginOpaqueFinish(
        initResponse.attempt_id,
        loginFinishResult.finishLoginRequest
      );

      const exportKeyBytes = new TextEncoder()
        .encode(loginFinishResult.exportKey)
        .subarray(0, 32);
      const decryptedMkBytes = await decryptWithKey(
        finishResponse.encrypted_mk_pw,
        exportKeyBytes
      );

      setVaultSession(finishResponse.session_token, decryptedMkBytes);
    } catch (err: unknown) {
      console.error("Failed to unlock vault:", err);
      setError("That password didn't work. Please check it and try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleSetup = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!password || !user?.email) return;

    if (password.length < 8) {
      setError("Password must be at least 8 characters long.");
      return;
    }

    if (password !== confirmPassword) {
      setError("Passwords do not match.");
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const regStartResult = await startOpaqueRegister(password);

      const regInitResponse = await registerOpaqueStart(
        user.email,
        regStartResult.registrationRequest
      );

      const regFinishResult = await finishOpaqueRegister(
        regStartResult.clientRegistrationState,
        regInitResponse.opaque_response
      );

      const masterKey = generateRandomBytes(32);

      const exportKeyBytes = new TextEncoder()
        .encode(regFinishResult.exportKey)
        .subarray(0, 32);
      const encryptedMkPw = await encryptWithKey(masterKey, exportKeyBytes);

      const regFinishResponse = await registerOpaqueFinish(
        regInitResponse.attempt_id,
        regFinishResult.registrationRecord,
        encryptedMkPw
      );

      try {
        // Best-effort: escrow payload submission if adviser has configured an escrow key.
      } catch (escrowErr) {
        console.warn("Adviser escrow payload submission failed:", escrowErr);
      }

      setVaultSession(regFinishResponse.session_token, masterKey);
      setViewState("success");
    } catch (err: unknown) {
      console.error("Failed to setup vault:", err);
      setError(
        "We couldn't set up your vault. Please try again. If the problem continues, contact support."
      );
    } finally {
      setLoading(false);
    }
  };

  const panelTitle =
    viewState === "login"
      ? "Open your vault"
      : viewState === "setup"
        ? "Create your vault password"
        : viewState === "reset"
          ? "Start over"
          : "Your vault is ready";

  const panelSubtitle =
    viewState === "login"
      ? "Enter your vault password to open your documents."
      : viewState === "setup"
        ? "Choose a password you will use whenever you open your vault. Secure Legacy cannot reset this password, so keep it somewhere safe."
        : viewState === "reset"
          ? "If you have forgotten your vault password, you can permanently delete this vault and create a new one."
          : "Taking you to your documents...";

  return (
    <div className="mx-auto w-full max-w-[460px] py-8">
      <div className="bg-card rounded-2xl border p-8 shadow-sm">
        <div className="flex flex-col items-center pb-2 text-center">
          <div
            className={
              viewState === "reset"
                ? "mx-auto mb-3 flex h-14 w-14 items-center justify-center rounded-full bg-red-100 text-red-600 dark:bg-red-950/40 dark:text-red-400"
                : "bg-primary/10 text-primary mx-auto mb-3 flex h-14 w-14 items-center justify-center rounded-full"
            }
          >
            {viewState === "reset" ? (
              <AlertTriangle className="h-6 w-6" />
            ) : (
              <Lock className="h-6 w-6" />
            )}
          </div>
          <h1 className="text-2xl font-bold tracking-tight">{panelTitle}</h1>
          <p className="text-muted-foreground mt-1.5 max-w-sm text-sm">
            {panelSubtitle}
          </p>
        </div>

        {viewState === "login" && (
          <form onSubmit={handleUnlock} className="mt-4 space-y-5">
            <div className="space-y-2">
              <Label htmlFor="unlock-password">Vault password</Label>
              <div className="relative">
                <Input
                  id="unlock-password"
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••••••"
                  required
                  disabled={loading}
                  className="pr-10"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="text-muted-foreground hover:text-foreground absolute top-2.5 right-3"
                >
                  {showPassword ? (
                    <EyeOff className="h-4 w-4" />
                  ) : (
                    <Eye className="h-4 w-4" />
                  )}
                </button>
              </div>
            </div>

            {error && (
              <div className="border-destructive/20 bg-destructive/10 text-destructive flex items-center gap-2.5 rounded-lg border p-3 text-sm">
                <ShieldAlert className="h-4 w-4 shrink-0" />
                <span>{error}</span>
              </div>
            )}

            <div className="flex flex-col gap-3 pt-2">
              <Button
                type="submit"
                disabled={loading || !password}
                className="w-full"
              >
                {loading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Opening your vault...
                  </>
                ) : (
                  <>
                    <KeyRound className="mr-2 h-4 w-4" />
                    Open vault
                  </>
                )}
              </Button>

              <div className="flex items-center justify-center gap-4 text-sm">
                <button
                  type="button"
                  onClick={goToReset}
                  disabled={loading}
                  className="text-muted-foreground hover:text-foreground underline-offset-4 hover:underline disabled:opacity-50"
                >
                  Forgot your vault password?
                </button>
              </div>
            </div>
          </form>
        )}

        {viewState === "setup" && (
          <form onSubmit={handleSetup} className="mt-4 space-y-4">
            <div className="space-y-2">
              <Label htmlFor="setup-password">Create vault password</Label>
              <div className="relative">
                <Input
                  id="setup-password"
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="At least 8 characters"
                  required
                  disabled={loading}
                  className="pr-10"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="text-muted-foreground hover:text-foreground absolute top-2.5 right-3"
                >
                  {showPassword ? (
                    <EyeOff className="h-4 w-4" />
                  ) : (
                    <Eye className="h-4 w-4" />
                  )}
                </button>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="confirm-password">Confirm vault password</Label>
              <Input
                id="confirm-password"
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="Re-enter password"
                required
                disabled={loading}
              />
            </div>

            {error && (
              <div className="border-destructive/20 bg-destructive/10 text-destructive flex items-center gap-2.5 rounded-lg border p-3 text-sm">
                <ShieldAlert className="h-4 w-4 shrink-0" />
                <span>{error}</span>
              </div>
            )}

            <div className="flex flex-col gap-3 pt-2">
              <Button
                type="submit"
                disabled={loading || !password || !confirmPassword}
                className="w-full"
              >
                {loading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Setting up your vault...
                  </>
                ) : (
                  "Create and open vault"
                )}
              </Button>
            </div>
          </form>
        )}

        {viewState === "reset" && (
          <div className="mt-4">
            <VaultResetPanel onBack={backToLogin} />
          </div>
        )}

        {viewState === "success" && (
          <div className="mt-4 flex flex-col items-center justify-center space-y-3 py-6">
            <div className="bg-primary/10 text-primary flex h-12 w-12 items-center justify-center rounded-full">
              <Loader2 className="h-6 w-6 animate-spin" />
            </div>
            <p className="text-primary font-medium">Vault opened</p>
          </div>
        )}
      </div>
    </div>
  );
}

/**
 * Owner-driven vault reset ("I forgot my password"), rendered inside the unlock
 * panel. Because the vault password unwraps the master key, a forgotten password
 * with no recovery key means the data is unrecoverable — the only way forward is
 * to throw the vault away and start over. This is authenticated purely by the
 * magic-link session (VaultIdentity), so it works even though the owner can't
 * unlock. After a successful reset the vault is back in `pending_registration`,
 * so reloading drops the owner into the normal "initialize your vault" flow.
 */
const HOLD_MS = 5000;

function VaultResetPanel({ onBack }: { onBack: () => void }) {
  const [vaults, setVaults] = useState<OwnedVaultItem[] | null>(null);
  const [isResetting, setIsResetting] = useState(false);
  const [done, setDone] = useState(false);
  // Final confirmation dialog shown only after the hold gesture completes.
  const [confirmOpen, setConfirmOpen] = useState(false);

  // Hold-to-confirm: the reset only fires after the button is held for HOLD_MS,
  // with the background filling left-to-right to show progress.
  const [holdProgress, setHoldProgress] = useState(0); // 0..1
  const rafRef = useRef<number | null>(null);
  const holdStartRef = useRef(0);
  const btnRef = useRef<HTMLButtonElement>(null);
  const [btnWidth, setBtnWidth] = useState(0);

  useEffect(() => {
    listOwnedVaults()
      .then(setVaults)
      .catch(() => setVaults([]));
  }, []);

  // Keep the fill's inner (white) label the full width of the button so its text
  // stays centred on the button rather than on the clipped fill region.
  useEffect(() => {
    const el = btnRef.current;
    if (!el) return;
    const update = () => setBtnWidth(el.offsetWidth);
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, [vaults, done]);

  useEffect(() => {
    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, []);

  // A vault user owns at most one vault in practice; take the first.
  const vault = vaults?.[0] ?? null;

  const handleReset = async () => {
    if (!vault) return;
    setIsResetting(true);
    try {
      // The server guards the destructive action against the vault email; the
      // owner is already authenticated via magic-link, so we send it directly.
      await resetVault(vault.client_id, vault.email);
      setDone(true);
      toast.success("Your old vault has been deleted.");
    } catch {
      toast.error("Could not reset the vault. Please try again.");
    } finally {
      setIsResetting(false);
    }
  };

  const cancelHold = () => {
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    rafRef.current = null;
    setHoldProgress(0);
  };

  const startHold = () => {
    if (isResetting || rafRef.current) return; // ignore double-starts (e.g. key repeat)
    holdStartRef.current = performance.now();
    const tick = () => {
      const p = Math.min(
        (performance.now() - holdStartRef.current) / HOLD_MS,
        1
      );
      setHoldProgress(p);
      if (p >= 1) {
        // Hold satisfied — retract the bar and open the final confirm dialog.
        rafRef.current = null;
        setHoldProgress(0);
        setConfirmOpen(true);
      } else {
        rafRef.current = requestAnimationFrame(tick);
      }
    };
    rafRef.current = requestAnimationFrame(tick);
  };

  if (vaults === null) {
    return (
      <div className="flex h-[120px] items-center justify-center">
        <Loader2 className="text-muted-foreground h-6 w-6 animate-spin" />
      </div>
    );
  }

  if (done) {
    return (
      <div className="flex flex-col items-center space-y-4 text-center">
        <p className="text-muted-foreground text-sm leading-relaxed">
          Your old vault, documents, and recovery arrangements have been
          permanently deleted. You can now create a new vault and password.
        </p>
        <Button className="w-full" onClick={() => window.location.reload()}>
          Set up a new vault
        </Button>
      </div>
    );
  }

  if (!vault) {
    return (
      <div className="space-y-4">
        <p className="text-muted-foreground text-sm leading-relaxed">
          There is no vault linked to this account, so there is nothing to
          delete.
        </p>
        <Button
          type="button"
          variant="ghost"
          onClick={onBack}
          className="w-full"
        >
          <ArrowLeft className="mr-2 h-4 w-4" />
          Back to password
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="border-destructive/20 bg-destructive/10 text-muted-foreground rounded-lg border p-3 text-sm leading-relaxed">
        Resetting{" "}
        <span className="text-foreground font-semibold">
          permanently deletes all documents and recovery arrangements
        </span>{" "}
        in this vault. This cannot be undone. Press and hold the button below to
        continue.
      </div>

      <div className="flex flex-col gap-3 pt-2">
        {(() => {
          const fillPct = isResetting ? 100 : holdProgress * 100;
          const label = isResetting ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Deleting vault...
            </>
          ) : holdProgress > 0 ? (
            "Keep holding…"
          ) : (
            "Hold to delete my vault"
          );
          return (
            <button
              ref={btnRef}
              type="button"
              disabled={isResetting}
              onPointerDown={startHold}
              onPointerUp={cancelHold}
              onPointerLeave={cancelHold}
              onPointerCancel={cancelHold}
              onKeyDown={(e) => {
                // Hold the spacebar to fill, mirroring press-and-hold.
                if (e.key === " " || e.key === "Spacebar") {
                  e.preventDefault(); // stop the native click-on-space
                  if (!e.repeat) startHold();
                }
              }}
              onKeyUp={(e) => {
                if (e.key === " " || e.key === "Spacebar") {
                  e.preventDefault();
                  cancelHold();
                }
              }}
              onBlur={cancelHold}
              aria-label="Press and hold, or hold the spacebar, to delete my vault"
              className="border-destructive focus-visible:ring-ring/50 relative h-10 w-full touch-none overflow-hidden rounded-md border-2 text-sm font-medium transition-opacity select-none focus-visible:ring-[3px] focus-visible:outline-none disabled:pointer-events-none disabled:opacity-50"
            >
              {/* Base (unfilled) label */}
              <span className="text-destructive absolute inset-0 flex items-center justify-center gap-2 px-3">
                {label}
              </span>
              {/* Fill layer, clipped to progress; inner label is button-width so it stays centred */}
              <span
                className="bg-destructive absolute inset-y-0 left-0 overflow-hidden"
                style={{
                  width: `${fillPct}%`,
                  transition:
                    holdProgress === 0 ? "width 150ms ease-out" : "none",
                }}
                aria-hidden="true"
              >
                <span
                  className="absolute inset-y-0 left-0 flex items-center justify-center gap-2 px-3 text-white"
                  style={{ width: btnWidth || "100%" }}
                >
                  {label}
                </span>
              </span>
            </button>
          );
        })()}

        <Button
          type="button"
          variant="ghost"
          onClick={onBack}
          disabled={isResetting}
          className="w-full"
        >
          <ArrowLeft className="mr-2 h-4 w-4" />
          Back to password
        </Button>
      </div>

      <ConfirmDialog
        open={confirmOpen}
        onOpenChange={setConfirmOpen}
        destructive
        title="Delete this vault permanently?"
        description="All documents and recovery arrangements will be deleted. This cannot be undone."
        confirmLabel="Delete vault"
        cancelLabel="Cancel"
        loading={isResetting}
        autoCloseOnConfirm={false}
        onConfirm={handleReset}
      />
    </div>
  );
}
